import { useState } from 'react';
import { Modal, Button, Input, Checkbox } from 'components/ui';
import { ShopSelect } from 'components/ShopSelect';
import { useToast } from 'components/toast/ToastProvider';
import { useVendor } from 'features/vendor';

function initialForm(account) {
  return {
    beneficiaryName: account?.beneficiaryName || '',
    accountNumber: account?.accountNumber || '',
    ifscCode: account?.ifscCode || '',
    email: account?.email || '',
    isPrimaryAccount: Boolean(account?.isPrimaryAccount),
  };
}

/**
 * Add or edit a bank account. Pass `account` to edit (the form is keyed on its
 * id by the parent so state resets per account).
 */
export function BankAccountModal({ isOpen, account, onClose, createAccount, updateAccount, saving }) {
  const toast = useToast();
  const { vendorId: activeVendorId } = useVendor();
  const isEditing = Boolean(account);
  const [form, setForm] = useState(() => initialForm(account));
  const [shopId, setShopId] = useState(() => activeVendorId);
  const [errors, setErrors] = useState({});

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!form.beneficiaryName.trim()) nextErrors.beneficiaryName = 'Enter the beneficiary name.';
    if (!form.accountNumber.trim()) nextErrors.accountNumber = 'Enter the account number.';
    if (!form.ifscCode.trim()) nextErrors.ifscCode = 'Enter the IFSC code.';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const input = {
      accountNumber: form.accountNumber.trim(),
      ifscCode: form.ifscCode.trim().toUpperCase(),
      beneficiaryName: form.beneficiaryName.trim(),
      email: form.email.trim() || undefined,
      isPrimaryAccount: form.isPrimaryAccount,
    };

    try {
      const result = isEditing
        ? await updateAccount(account.id, input)
        : await createAccount(input, shopId);

      // These mutations report failure via a non-empty `error` field.
      if (result?.error) {
        toast.error(isEditing ? 'Could not update account' : 'Could not add account', result.error);
        return;
      }
      toast.success(
        isEditing ? 'Account updated' : 'Account added',
        result?.message || undefined,
      );
      onClose();
    } catch (err) {
      toast.error('Something went wrong', err.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit bank account' : 'Add bank account'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={saving}>
            {isEditing ? 'Save changes' : 'Add account'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!isEditing && <ShopSelect label="Add to shop" value={shopId} onChange={setShopId} />}
        <Input
          label="Beneficiary name"
          required
          value={form.beneficiaryName}
          onChange={(event) => setField('beneficiaryName', event.target.value)}
          error={errors.beneficiaryName}
        />
        <Input
          label="Account number"
          required
          inputMode="numeric"
          value={form.accountNumber}
          onChange={(event) => setField('accountNumber', event.target.value)}
          error={errors.accountNumber}
        />
        <Input
          label="IFSC code"
          required
          value={form.ifscCode}
          onChange={(event) => setField('ifscCode', event.target.value)}
          error={errors.ifscCode}
        />
        <Input
          label="Email"
          type="email"
          placeholder="Optional"
          value={form.email}
          onChange={(event) => setField('email', event.target.value)}
        />
        <Checkbox
          label="Set as primary payout account"
          checked={form.isPrimaryAccount}
          onChange={(event) => setField('isPrimaryAccount', event.target.checked)}
        />
      </div>
    </Modal>
  );
}

export default BankAccountModal;
